import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Trash2, Download } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCraftsCart } from "@/context/CraftsCartContext";
import { toast } from "sonner";

export default function CraftsCart() {
  const { items, remove, total, open, setOpen } = useCraftsCart();
  const { format, isConverted } = useCurrency();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null); // {code, discount_amount}
  const [applying, setApplying] = useState(false);

  const applyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: code.trim(), subtotal: total });
      setPromo({ code: data.code, discount_amount: data.discount_amount });
      toast.success(`Code ${data.code} applied`);
    } catch (e) {
      setPromo(null);
      toast.error(e.response?.data?.detail || "That code isn't valid.");
    }
    setApplying(false);
  };

  const distinctCount = new Set(items.map((i) => i.craft_id)).size;
  const hasPhysical = items.some((i) => i.physical);
  const bundleDiscount = distinctCount >= 3 ? total * 0.15 : 0;
  const codeDiscount = promo ? Math.min(promo.discount_amount, total) : 0;
  const bundleWins = bundleDiscount > 0 && bundleDiscount >= codeDiscount;
  const discount = Math.max(codeDiscount, bundleDiscount);
  const discountLabel = bundleWins ? "Bundle · save 15%" : promo?.code;
  const payable = Math.max(0, total - discount);

  const checkout = async () => {
    if (!items.length) return;
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter your email so we can send your files.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/crafts/checkout", {
        items: items.map((i) => ({ craft_id: i.craft_id, tier_id: i.tier_id || null })),
        origin_url: window.location.origin,
        email: email.trim(),
        discount_code: promo?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 z-[60]" onClick={() => setOpen(false)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.aside
            data-testid="crafts-cart"
            className="fixed right-0 top-0 h-full w-full max-w-md z-[61] flex flex-col border-l border-gold/20 text-cream"
            style={{ backgroundColor: "#2E1A13" }}
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-gold/15">
              <p className="font-serif text-xl tracking-[0.08em]">Your bag</p>
              <button onClick={() => setOpen(false)} data-testid="crafts-cart-close" className="text-cream/60 hover:text-gold transition-colors"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <p className="text-cream/50 text-sm py-16 text-center">Your bag is empty.</p>
              ) : (
                <div className="space-y-4">
                  {items.map((it) => (
                    <div key={`${it.craft_id}-${it.tier_id || "base"}`} data-testid={`crafts-cart-item-${it.slug}`} className="flex items-center gap-4">
                      <div className="w-14 h-16 bg-espresso/40 overflow-hidden shrink-0">
                        {it.cover_image ? <img src={mediaUrl(it.cover_image)} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-cream text-sm truncate">{it.title}</p>
                        <p className="text-cream/60 text-xs tabular-nums mt-0.5">{format(it.price)}</p>
                      </div>
                      <button onClick={() => remove(it.craft_id, it.tier_id)} data-testid={`crafts-cart-remove-${it.slug}`}
                        className="text-cream/40 hover:text-wine transition-colors p-1"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-gold/15 px-6 py-5 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                    data-testid="crafts-cart-code" placeholder="Promo code"
                    className="flex-1 bg-transparent border border-cream/20 px-4 py-2.5 text-sm text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors uppercase tracking-wide" />
                  <button onClick={applyCode} disabled={applying} data-testid="crafts-cart-apply"
                    className="border border-cream/25 text-cream/80 px-4 py-2.5 text-[0.64rem] tracking-[0.16em] uppercase hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
                    {applying ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
                  </button>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-cream/60">
                    <span>Subtotal</span><span className="tabular-nums">{format(total)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex items-center justify-between text-sm text-gold" data-testid="crafts-cart-discount">
                      <span>{discountLabel}</span><span className="tabular-nums">− {format(discount)}</span>
                    </div>
                  )}
                  {distinctCount > 0 && distinctCount < 3 && (
                    <p data-testid="crafts-bundle-nudge" className="text-[0.62rem] tracking-[0.06em] text-gold/80 leading-relaxed pt-1">
                      Add all three guides and save 15% at checkout.
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[0.66rem] tracking-[0.2em] uppercase text-cream/50">Total</span>
                    <span className="font-serif text-xl tabular-nums" data-testid="crafts-cart-total">{format(payable)}</span>
                  </div>
                </div>
                {isConverted && <p className="text-[0.6rem] tracking-[0.14em] uppercase text-cream/40 -mt-2">Charged in GBP · approx. conversion</p>}
                {hasPhysical && (
                  <p data-testid="crafts-shipping-note" className="text-[0.62rem] tracking-[0.06em] text-gold/85 leading-relaxed border border-gold/25 rounded px-3 py-2">
                    Your starter kit ships to you — we'll collect your delivery address securely at checkout.
                  </p>
                )}
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="crafts-cart-email" placeholder="Email for delivery"
                  className="w-full bg-transparent border border-cream/20 px-4 py-3 text-sm text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors" />
                <button onClick={checkout} disabled={busy} data-testid="crafts-cart-checkout"
                  className="w-full flex items-center justify-center gap-2 bg-gold text-cocoa px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  {busy ? "Starting…" : "Checkout & Download"}
                </button>
                <p className="text-[0.6rem] text-cream/40 leading-relaxed">Secure checkout. Download links appear instantly after payment and are emailed to you.</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
